import React, { useEffect } from "react";
import { ButtonSize, ButtonTypes } from "utils/interface.utils";
import { useSetState, toastifySuccess, toastifyError } from "utils/functions.utils";
import { STRINGS } from "constants/strings.constant";
import Assets from "imports/assets.import";
import PrimaryButton from "../primary_button/primary_button.ui";
import AdminConfirmModal from "../admin_confirm_modal/admin_confirm_modal.ui";
import OutsideClickHandlerComponent from "../outside_click_handler/outside_click_handler.ui";
import "./admin_actions.ui.scss";

interface IAdminActions {
  kind: "user" | "project";
  isBlocked: boolean;
  // Both reject when the request fails; a toast reports the result either way.
  onBlockChange: (blocked: boolean) => Promise<void>;
  onDelete: () => Promise<void>;
}

type PendingAction = "block" | "delete" | null;

const AdminActions = (props: IAdminActions) => {
  const { kind, isBlocked, onBlockChange, onDelete } = props;
  const strings = STRINGS.adminActions[kind];
  const [state, setState] = useSetState({ pending: null as PendingAction, loading: false, isMenuOpen: false });

  useEffect(() => {
    if (!state.isMenuOpen) return undefined;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setState({ isMenuOpen: false });
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [state.isMenuOpen]);

  const ask = (pending: PendingAction) => {
    setState({ pending, isMenuOpen: false });
  };

  const confirm = async () => {
    const action = state.pending;
    setState({ loading: true });
    try {
      if (action === "delete") await onDelete();
      else await onBlockChange(!isBlocked);
      toastifySuccess(action === "delete" ? strings.deleted : isBlocked ? strings.unblocked : strings.blocked);
      setState({ loading: false, pending: null });
    } catch (err) {
      console.error("Admin action failed", err);
      toastifyError(action === "delete" ? strings.deleteFailed : strings.updateFailed);
      setState({ loading: false, pending: null });
    }
  };

  const isDelete = state.pending === "delete";
  const modalTitle = isDelete ? strings.deleteTitle : isBlocked ? strings.unblockTitle : strings.blockTitle;
  const modalMessage = isDelete ? strings.confirmDelete : isBlocked ? strings.confirmUnblock : strings.confirmBlock;

  return (
    <div className="admin_actions">
      <OutsideClickHandlerComponent onOutsideClick={() => setState({ isMenuOpen: false })}>
        <div className="admin_actions_wrap">
          <PrimaryButton
            text={STRINGS.adminActions.actions}
            size={ButtonSize.MEDIUM}
            type={ButtonTypes.OUTLINED}
            className="admin_actions_trigger"
            right_icon={Assets.chevron_down_ink}
            onClick={() => setState({ isMenuOpen: !state.isMenuOpen })}
          />
          {state.isMenuOpen && (
            <div className="admin_actions_menu" role="menu">
              <div className="admin_actions_item" role="menuitem" aria-label={isBlocked ? strings.unblock : strings.block} tabIndex={0} onClick={() => ask("block")}>
                <img src={Assets.ban_ink} alt="" className="admin_actions_item_icon" />
                <div>
                  <p className="admin_actions_item_title">{isBlocked ? strings.unblock : strings.block}</p>
                  <span className="admin_actions_item_hint">{strings.blockHint}</span>
                </div>
              </div>
              <div className="admin_actions_item admin_actions_item_danger" role="menuitem" aria-label={strings.delete} tabIndex={0} onClick={() => ask("delete")}>
                <img src={Assets.trash_danger_red} alt="" className="admin_actions_item_icon" />
                <div>
                  <p className="admin_actions_item_title">{strings.delete}</p>
                  <span className="admin_actions_item_hint">{strings.deleteHint}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </OutsideClickHandlerComponent>
      <AdminConfirmModal
        open={state.pending !== null}
        title={modalTitle}
        message={modalMessage}
        confirmText={STRINGS.adminActions.confirm}
        cancelText={STRINGS.adminActions.cancel}
        danger={isDelete}
        loading={state.loading}
        onConfirm={confirm}
        onCancel={() => setState({ pending: null })}
      />
    </div>
  );
};

export default AdminActions;
